import { useMemo } from 'react';
import { useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { FACILITY_KINDS, buildTerminalOptions, parseFacilityValue } from '@/config/formOptions';
import { DependentDropdownField } from './DependentDropdownField';

/**
 * Props:
 *   sites     the session's sites, which carry their terminals
 *   clears    downstream field names to reset when the terminal changes
 *   label     overrides the plain field name, for a form that marks it optional
 *   required  defaults to false: a contract's terminal is optional, and then the list
 *             carries a None row — the only way back out of a terminal already picked.
 *             Only enforced when the site has terminals; one with none can't offer a pick
 *   rules     extra validation rules, e.g. the picked terminal must carry a fixed material
 *   disabled  locked once something downstream depends on it
 */
export const TerminalField = ({
  sites = [],
  clears,
  label,
  required = false,
  rules,
  disabled = false,
}) => {
  const t = useTranslations();
  const { values } = useFormState();

  const facility = useMemo(() => parseFacilityValue(values.facilityId), [values.facilityId]);
  const siteId = facility.kind === FACILITY_KINDS.site ? facility.id : null;

  const terminals = useMemo(
    () => buildTerminalOptions(sites.find((entry) => String(entry.id) === String(siteId))),
    [sites, siteId],
  );
  const isRequired = required && terminals.length > 0;

  const options = useMemo(
    () =>
      required || terminals.length === 0
        ? terminals
        : [{ value: '', label: t('COMMON_NONE') }, ...terminals],
    [terminals, required, t],
  );

  return (
    <DependentDropdownField
      name="terminalId"
      label={label ?? t('FIELD_TERMINAL')}
      placeholder={t('FORM_SELECT_TERMINAL')}
      emptyMessage={siteId ? t('FORM_TERMINAL_EMPTY') : t('FORM_SELECT_FACILITY_FIRST')}
      options={options}
      clears={clears}
      required={isRequired}
      rules={rules}
      disabled={disabled}
    />
  );
};
